// eslint-disable-next-line jira/filename-case
import React, { type ComponentType, useCallback } from 'react';
import noop from 'lodash/noop';
import { v4 as uuid } from 'uuid';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createSubscriber,
	type Action,
	createStateHook,
	createActionsHook,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type SubscriberComponent,
} from '@atlassian/react-sweet-state';
import { CreateIssueUFOExperience } from '@jira/issue-create__issue-create-analytics/src/services/experiences/index.tsx';
import { GLOBAL_EVENT_NAMESPACE } from '@jira/issue-create__issue-create-common-types/src/common/constants/index.tsx';
import type { Project } from '@jira/issue-create__issue-create-common-types/src/common/types/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@jira/issue-create__issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { getGICLifeCycleEvents } from '@jira/issue-create__issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { TRIGGER_POINT_KEY_NOT_DEFINED } from './constants.tsx';
import { useSitePickerContext } from './controllers/use-site-picker-context/index.tsx';
import {
	type TriggerPointKeyType,
	type MinimizableModalView,
	type PersistentIssueCreateModalView,
	type State,
	type GICPayload,
	ISSUE_CREATE_SUBMIT_BEHAVIOUR,
} from './types.tsx';
import {
	discoverabilityPushModalViewOverride,
	getNextModalView,
	isPartOfDiscoverabilityTriggers,
	shouldTriggerModernisedIssueCreate,
	startGlobalIssueCreateMetrics,
} from './utils.tsx';

const traceModernisedGicPress = (isSubsequentLoad: boolean): void => {
	if (fg('nike_united_fix_ufo_error')) {
		return;
	}

	if (isSubsequentLoad) {
		traceUFOPress('global-issue.create-modal-modernised-subsequent');
	} else {
		traceUFOPress('global-issue.create-modal-modernised');
	}
};

export const initialState: State = {
	inProgressIssueID: undefined,
	isModalOpen: false,
	isSubsequentLoad: false,
	// isSubTaskCreationOpen is used for creating subtasks with new GIC
	isSubTaskCreationOpen: false,
	// isCreateLinkedIssue is used for creating linked issues with new GIC
	isCreateLinkedIssue: false,
	wasStateRestored: false,
	// payload can be used to control the initial values displayed in the GIC Modal
	payload: null,
	// id for the GIC Modal component
	id: 'create-issue-dialog',
	// title for the GIC modal
	title: null,
	// sectionMessage is used to show an info banner at the top of the GIC modal
	sectionMessage: null,
	// submitErrorMessage is used to show an error banner at the top of the GIC modal
	submitErrorMessage: undefined,
	// globalEventNamespaces is the list of event namespaces to which data of created issues will be published
	globalEventNamespaces: [GLOBAL_EVENT_NAMESPACE],
	// fieldsToAddInPublishedData: list of additioanl fields that should be appended in the data published for
	// created issues. By default we publish summary, status, assignee, duedate, project, issuetype & priority
	fieldsToAddInPublishedData: [],
	// Used on Create Incident from Alerts functionality. Only relevant for Opsgenie alert pages
	alertsToBeLinked: undefined,
	// if onIssueCreate is passed for every issue created in the session onIssueCreate is called with
	// the data of each issue created
	onIssueCreate: null,
	onClose: null,
	// onSiteSelection is called when a site is selected in the site picker
	onSiteSelection: null,
	// onProjectSelection is called when a project is selected in the project picker
	onProjectSelection: null,
	// only show required fields
	displayOnlyRequiredFields: false,
	// disables the success flag
	disableCreationSuccessFlag: false,
	// toggle the 'create another issue' checkbox
	displayCreateAnotherIssueToggle: true,
	// toggle display of site picker field.
	displaySitePicker: false,
	// do not allow user to change issue project
	disableProjectDropdown: false,
	// do not allow user to change issue type
	disableIssueTypeDropdown: false,
	// triggerPoinKey can be used to identify specific trigger point of new GIC
	triggerPointKey: TRIGGER_POINT_KEY_NOT_DEFINED,
	// context can be used to pass data like rank/board data which will be used as payload for issue creation
	context: undefined,
	// the view state for the minimizable modal
	minimizableModalView: 'modal',
	// the modal view maybe persistent depending on conditions, if this value is present, it may override the `minimizableModalView` during `openIssueCreateModal`.
	persistentModalView: undefined,
	// whether we should display mini modal by default for the first time. If this is true, it will override the view state to `mini` during `openIssueCreateModal`.
	shouldPushMiniModalDiscoverability: undefined,
	// whether we should display mini modal spotlight for the first time.
	shouldShowMiniModalSpotlight: undefined,
	// modal was attempted to be opened when already opened
	wasModalRetriggered: false,
	// if there is an existing gic open - we only apply the next state if they discard their current issue
	openIssueCreateModalNextState: {},
	// when an issue is discarded we will reinitialise the gic modal
	shouldInitFetchConfig: false,
	interactedWithModalChangerButtons: false,
	// open modal for the jql board trigger point
	isModalLoadInContext: false,
	// opt-out of the minimizable GIC modal
	disableMinimizableModal: false,
	// switch to control the hide and display of minimizable buttons
	displayMinimizableButtons: true,
	// switch to control the hide and display of header action container
	displayHeaderActionContainer: true,
	// do not override "use request type fields" toggle state
	overrideUseRequestTypeFieldsToggleState: undefined,
	/**
	 * this is for applying the container styles on the GIC containers except modal view
	 */
	containerStyle: {},
	// this flag is for enable the minidesign for a trigger point without being in mini view
	isMiniDesignEnabled: false,
	// this flag is for the GIC multiple layout experiment by Makkuro
	isEnrolledAndTargetedForGicMultipleLayouts: false,
	// optional sessionId used for the current issue creation session
	sessionId: undefined,
	// optional component - when supplied, this wraps every field as children within the GIC modal
	FieldWrapperComponent: undefined,
	// optional component - Renders external component above form fields
	ExternalControls: undefined,
	// optional ID of the jql swimlane we are creating issues in
	jqlSwimlaneId: undefined,
	// prefilled custom Fields if any
	prefilledCustomFields: {},
	// callback for ecosystem API to execute
	ecosystemCallback: undefined,
	// product where issue create anywhere modal is used
	product: undefined,
	// what's the consumer of the issue create anywhere modal in a product
	actionSource: undefined,
	// Field IDs that will remain visible even when the smart fields feature recommends them to be hidden.
	optOutSmartFieldIds: undefined,
	// refrence of the button from where GIC modal is triggered
	triggerButtonRef: null,
	// reference to the element to focus when the modal is closed
	returnFocusRef: undefined,
	// when true, indicates the user was redirected from the modern modal to the old modal (e.g., for unsupported project types)
	isRedirectedFromModernModal: false,
	// when true, a transition between old and new modal is in progress
	isTransitioningBetweenOldAndNewModal: false,
	shouldUseLegacyCreateExperience: false,
	// controls whether GIC creates an issue on submit or fires onSaveEditsWithoutIssueCreation.
	submitBehaviour: ISSUE_CREATE_SUBMIT_BEHAVIOUR.CREATE_ISSUE,
	// called with the full form field payload when submitBehaviour is 'saveEditsWithoutIssueCreation' and the user clicks the submit button. No issue is created in this case.
	onSaveEditsWithoutIssueCreation: undefined,
};

export const actions = {
	restoreState:
		(
			stateToRestore: Partial<State>,
			isTransitioningBetweenExistingAndNewModal?: boolean,
		): Action<State> =>
		({ setState, getState }) => {
			// During modal transitions (switchToModernModal / triggerFallbackToOldModal),
			// skip restore entirely to prevent stale draft data from overwriting the transition state.
			if (isTransitioningBetweenExistingAndNewModal && fg('create_work_item_modernization')) {
				return;
			}
			// If the user just discarded the existing GIC (wasModalRetriggered was reset to false),
			// skip restoring stale IndexedDB state that still has wasModalRetriggered: true.
			// This prevents the discard modal and docked view from reopening after discard.
			const currentState = getState();
			if (
				!currentState.wasModalRetriggered &&
				stateToRestore.wasModalRetriggered &&
				expVal('create_work_item_modernization_exp', 'isEnabled', false)
			) {
				return;
			}
			const { isSubsequentLoad, isModalOpen } = getState();
			if (!isModalOpen && stateToRestore.isModalOpen) {
				const isModernisedTrigger = Boolean(
					stateToRestore.triggerPointKey &&
					shouldTriggerModernisedIssueCreate(stateToRestore.triggerPointKey),
				);
				startGlobalIssueCreateMetrics(isSubsequentLoad, isModernisedTrigger);
				if (isModernisedTrigger) {
					traceModernisedGicPress(isSubsequentLoad);
				}
			}
			// Don't let stale persisted state close a modal that was just opened.
			// The openIssueCreateModal action sets isModalOpen inside a setTimeout(0),
			// and restoreState from IndexedDB can race and overwrite it.
			if (isModalOpen && !stateToRestore.isModalOpen) {
				return;
			}
			setState(stateToRestore);
		},
	openIssueCreateModal:
		(nextState?: GICPayload): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			if (currentState.isModalOpen) {
				/* If there is an existing gic open then we will defer the state change to prevent
                       clashing with the issue in creation. */
				setState({
					wasModalRetriggered: true,
					openIssueCreateModalNextState: nextState,
				});
			} else {
				const isModernisedTrigger = Boolean(
					nextState?.triggerPointKey &&
					shouldTriggerModernisedIssueCreate(nextState.triggerPointKey),
				);
				startGlobalIssueCreateMetrics(currentState.isSubsequentLoad, isModernisedTrigger);
				if (isModernisedTrigger) {
					traceModernisedGicPress(currentState.isSubsequentLoad);
				}
				const isDiscoverabilityPushEnabled =
					!currentState.isEnrolledAndTargetedForGicMultipleLayouts &&
					!expVal('create_work_item_modernization_exp', 'isEnabled', false);
				const isDiscoPushTriggerPoint =
					isDiscoverabilityPushEnabled &&
					isPartOfDiscoverabilityTriggers(nextState?.triggerPointKey);
				const nextModalView = isDiscoverabilityPushEnabled
					? getNextModalView(
							isDiscoPushTriggerPoint,
							currentState.persistentModalView,
							initialState.minimizableModalView,
						)
					: 'modal';
				/*
				 * This is done to always focus the Issue Create Modal trigger button whenever it's clicked
				 * so that we can store the reference of this
				 * and use that to re-focus the button whenever the modal is closed
				 */
				let triggerButton: HTMLButtonElement | null;
				const handleButtonClick = (event: Event) => {
					// oxlint-disable-next-line typescript/consistent-type-assertions
					const triggerButtonTarget = event.target as HTMLElement;
					triggerButton = triggerButtonTarget?.closest('button');
					triggerButton?.focus();
				};
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.addEventListener('click', handleButtonClick);
				setTimeout(() => {
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					document.removeEventListener('click', handleButtonClick);
				}, 0);
				setTimeout(() => {
					setState({
						...initialState,
						...(isDiscoverabilityPushEnabled && {
							minimizableModalView: nextModalView,
							persistentModalView: currentState.persistentModalView,
							...discoverabilityPushModalViewOverride(
								currentState.shouldPushMiniModalDiscoverability,
								isDiscoPushTriggerPoint,
								nextModalView,
							),
						}),
						inProgressIssueID: uuid(),
						isSubsequentLoad: currentState.isSubsequentLoad,
						isModalOpen: true,
						wasModalRetriggered: currentState.isModalOpen,
						triggeredViaHotKey: !!nextState?.triggeredViaHotKey,
						...nextState,
						/* oxlint-disable typescript/consistent-type-assertions */
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						triggerButtonRef: triggerButton || (document.activeElement as HTMLButtonElement),
						/* oxlint-enable typescript/consistent-type-assertions */
					});
				}, 0);
			}
		},
	closeIssueCreateModal:
		(hasIssueCreated?: boolean): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			const isDiscoverabilityPushEnabled = !currentState.isEnrolledAndTargetedForGicMultipleLayouts;
			const isValidDiscoPushTriggerPoint =
				isDiscoverabilityPushEnabled &&
				isPartOfDiscoverabilityTriggers(currentState.triggerPointKey);
			const shouldUpdatePersistentModalView =
				isDiscoverabilityPushEnabled && hasIssueCreated && isValidDiscoPushTriggerPoint;
			const shouldPersistDiscoverabilityState =
				isDiscoverabilityPushEnabled &&
				currentState.shouldPushMiniModalDiscoverability &&
				!isValidDiscoPushTriggerPoint;
			setState({
				...initialState,
				...{ callbackPayload: undefined },
				...(shouldPersistDiscoverabilityState && {
					shouldPushMiniModalDiscoverability: true,
				}),
				...(isDiscoverabilityPushEnabled && {
					persistentModalView: currentState.persistentModalView,
				}),
				...(shouldUpdatePersistentModalView && {
					persistentModalView: currentState.minimizableModalView === 'mini' ? 'mini' : 'modal',
				}),
				isModalOpen: false,
				isSubsequentLoad: true,
			});
			CreateIssueUFOExperience.abort({
				metadata: { wasStateRestored: getState().wasStateRestored },
			});
		},
	triggerFallbackToOldModal:
		(project: Project, options?: { forceLegacyCreateExperience?: boolean }): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			// The rest of the payload (parentId, issueType, defaultValues) only describes the project
			// the modal was triggered with. Keep it when we hand back to the old modal for that same
			// project — otherwise context-carrying trigger points such as create subtask lose their
			// parent and issue type and degrade into a blank create form. When the fallback is caused
			// by the user picking a different project, the payload no longer applies and is dropped.
			const isFallbackForTriggeringProject =
				currentState.payload?.project?.projectId !== undefined &&
				currentState.payload.project.projectId === project.value &&
				fg('create_modernization_ga_fixes_drop_6');
			const shouldRetainLinkedIssueContextOnly =
				currentState.isCreateLinkedIssue && fg('fix_linked_issue_on_project_switch_gic');
			const payloadForLinkedIssueContextOnly = {
				project: { projectId: project.value, projectType: project.projectType },
				...(currentState.payload?.defaultValues?.issueLinks !== undefined && {
					defaultValues: {
						issueLinks: currentState.payload.defaultValues.issueLinks,
					},
				}),
				...(currentState.payload?.additionalContext?.linkedIssue && {
					additionalContext: {
						linkedIssue: currentState.payload.additionalContext.linkedIssue,
					},
				}),
			};
			let payload = { project: { projectId: project.value, projectType: project.projectType } };
			if (options?.forceLegacyCreateExperience || isFallbackForTriggeringProject) {
				payload = {
					...currentState.payload,
					project: { projectId: project.value, projectType: project.projectType },
				};
			} else if (shouldRetainLinkedIssueContextOnly) {
				payload = payloadForLinkedIssueContextOnly;
			}
			setState({
				...currentState,
				isTransitioningBetweenOldAndNewModal: true,
				...(options?.forceLegacyCreateExperience && {
					shouldUseLegacyCreateExperience: true,
				}),
				payload,
			});
		},
	/**
	 * Switches from the old modal to the modern modal by closing and reopening.
	 * Uses a single setState to avoid intermediate renders where payload is cleared.
	 * Publishes MODAL_CLOSED to clean up the old in-progress issue from IndexedDB
	 * so stale draft data isn't restored after the transition.
	 */
	switchToModernModal:
		(project: Project): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			// Clean up the old in-progress issue from IndexedDB before switching
			const { publish } = getGICLifeCycleEvents();
			publish(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, {
				inProgressIssueID: currentState.inProgressIssueID ?? '',
				createdIssues: [],
			});
			startGlobalIssueCreateMetrics(true, true);
			traceModernisedGicPress(true);
			// Reset to initial state but immediately set the new modal state in a single setState
			// to avoid intermediate renders where payload is cleared
			setState({
				...initialState,
				isModalOpen: true,
				isSubsequentLoad: true,
				isTransitioningBetweenOldAndNewModal: true,
				shouldInitFetchConfig: true,
				triggerPointKey: currentState.triggerPointKey,
				payload: {
					project: {
						projectId: project.value,
						projectType: project.projectType,
						isSimplified: project.isSimplified,
					},
				},
			});
		},
	clearSectionMessage:
		(): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			setState({
				...currentState,
				sectionMessage: null,
			});
		},
	setDisplayOnlyRequiredFields:
		(nextState: boolean): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			setState({ ...currentState, displayOnlyRequiredFields: nextState });
		},
	setMinimizableModalView:
		(minimizableModalView: MinimizableModalView): Action<State> =>
		({ setState }) => {
			setState({
				minimizableModalView,
				interactedWithModalChangerButtons: true,
			});
		},
	resetWasModalRetriggered:
		(applyNextState = false): Action<State> =>
		({ setState, getState }) => {
			if (applyNextState) {
				const currentState = getState();
				const nextTriggerPointKey =
					currentState.openIssueCreateModalNextState?.triggerPointKey ??
					currentState.triggerPointKey;
				const isModernisedTrigger = Boolean(
					nextTriggerPointKey && shouldTriggerModernisedIssueCreate(nextTriggerPointKey),
				);
				startGlobalIssueCreateMetrics(currentState.isSubsequentLoad, isModernisedTrigger);
				if (isModernisedTrigger) {
					traceModernisedGicPress(currentState.isSubsequentLoad);
				}
				setState({
					...initialState,
					...(!currentState.isEnrolledAndTargetedForGicMultipleLayouts && {
						persistentModalView: currentState.persistentModalView,
					}),
					inProgressIssueID: currentState.inProgressIssueID,
					isSubsequentLoad: currentState.isSubsequentLoad,
					isModalOpen: true,
					wasModalRetriggered: false,
					minimizableModalView: 'modal',
					openIssueCreateModalNextState: {},
					shouldInitFetchConfig: true,
					...currentState.openIssueCreateModalNextState,
				});
			} else {
				setState({
					minimizableModalView: 'modal',
					wasModalRetriggered: false,
					openIssueCreateModalNextState: {},
				});
			}
		},
	resetShouldInitFetchConfig:
		(): Action<State> =>
		({ setState }) => {
			setState({
				shouldInitFetchConfig: false,
			});
		},
	setCallbackPayload:
		(callbackPayload: Required<GICPayload>['callbackPayload']['data']): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			setState({
				callbackPayload: {
					id: currentState.callbackPayload?.id ?? '',
					data: callbackPayload,
				},
			});
		},
	setShouldPushMiniModalDiscoverability:
		(shouldPushMiniModalDiscoverability?: boolean): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			if (currentState.isEnrolledAndTargetedForGicMultipleLayouts) {
				return;
			}
			setState({
				shouldPushMiniModalDiscoverability,
			});
		},
	dismissModalSpotlight:
		(): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			if (currentState.isEnrolledAndTargetedForGicMultipleLayouts) {
				return;
			}
			setState({
				shouldShowMiniModalSpotlight: false,
			});
		},
	setPersistentModalView:
		(persistentModalView: PersistentIssueCreateModalView): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			if (currentState.isEnrolledAndTargetedForGicMultipleLayouts) {
				return;
			}
			setState({
				persistentModalView,
			});
		},
	removeAlertFromAlertsToBeLinked:
		(alertId: string): Action<State> =>
		({ setState, getState }) => {
			const currentState = getState();
			const alertsToBeLinked = currentState.alertsToBeLinked?.filter((entry) => entry !== alertId);
			setState({
				alertsToBeLinked,
			});
		},
	setSessionId:
		(sessionId: string): Action<State> =>
		({ setState }) => {
			setState({ sessionId });
		},
};

export type Actions = typeof actions;

export const noopActions: ReturnType<typeof useTriggerIssueCreateModalActions> = {
	restoreState: (
		_stateToRestore: Partial<State>,
		_isTransitioningBetweenExistingAndNewModal?: boolean,
	): void => noop(),
	openIssueCreateModal: (_nextState?: GICPayload): void => noop(),
	closeIssueCreateModal: (_hasIssueCreated?: boolean): void => noop(),
	triggerFallbackToOldModal: (
		_project: Project,
		_options?: { forceLegacyCreateExperience?: boolean },
	): void => noop(),
	switchToModernModal: (_project: Project): void => noop(),
	clearSectionMessage: (): void => noop(),
	setDisplayOnlyRequiredFields: (_nextState: boolean): void => noop(),
	setMinimizableModalView: (_minimizableModalView: MinimizableModalView): void => noop(),
	resetWasModalRetriggered: (_applyNextState?: boolean): void => noop(),
	resetShouldInitFetchConfig: (): void => noop(),
	setCallbackPayload: (_callbackPayload: Required<GICPayload>['callbackPayload']['data']): void =>
		noop(),
	setShouldPushMiniModalDiscoverability: (_shouldPushMiniModalDiscoverability?: boolean): void =>
		noop(),
	dismissModalSpotlight: (): void => noop(),
	setPersistentModalView: (_persistentModalView: PersistentIssueCreateModalView): void => noop(),
	removeAlertFromAlertsToBeLinked: (_alertId: string): void => noop(),
	setSessionId: (_sessionId: string): void => noop(),
};

const store = createStore<State, Actions>({
	name: 'issue-create.issue-create-form.use-trigger-issue-create-modal',
	initialState,
	actions,
});

const useTriggerIssueCreateModalState: HookStateFunction<State, void> = createStateHook(store);
const useTriggerIssueCreateModalActions: HookActionsFunction<
	BoundActions<
		State,
		{
			restoreState: (
				stateToRestore: Partial<State>,
				isTransitioningBetweenExistingAndNewModal?: boolean,
			) => Action<State>;
			openIssueCreateModal: (nextState?: GICPayload) => Action<State>;
			closeIssueCreateModal: (hasIssueCreated?: boolean) => Action<State>;
			triggerFallbackToOldModal: (
				project: Project,
				options?: { forceLegacyCreateExperience?: boolean },
			) => Action<State>;
			switchToModernModal: (project: Project) => Action<State>;
			clearSectionMessage: () => Action<State>;
			setDisplayOnlyRequiredFields: (nextState: boolean) => Action<State>;
			setMinimizableModalView: (minimizableModalView: MinimizableModalView) => Action<State>;
			resetWasModalRetriggered: (applyNextState?: boolean) => Action<State>;
			resetShouldInitFetchConfig: () => Action<State>;
			setCallbackPayload: (
				callbackPayload: Required<GICPayload>['callbackPayload']['data'],
			) => Action<State>;
			setShouldPushMiniModalDiscoverability: (
				shouldPushMiniModalDiscoverability?: boolean,
			) => Action<State>;
			dismissModalSpotlight: () => Action<State>;
			setPersistentModalView: (
				persistentModalView: PersistentIssueCreateModalView,
			) => Action<State>;
			removeAlertFromAlertsToBeLinked: (alertId: string) => Action<State>;
			setSessionId: (sessionId: string) => Action<State>;
		}
	>
> = createActionsHook(store);

type Attributes = {
	gicViewState?: MinimizableModalView;
	isSubtask?: boolean;
	triggerPointKey?: TriggerPointKeyType;
	product?: string;
	actionSource?: string;
	addChildWorkItemTriggerSource?: string;
};

export const useTriggerIssueCreateModal = (): [
	ReturnType<typeof useTriggerIssueCreateModalState>,
	ReturnType<typeof useTriggerIssueCreateModalActions>,
] => {
	const createState = useTriggerIssueCreateModalState();
	const createActions = useTriggerIssueCreateModalActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { openIssueCreateModal, ...otherActions } = createActions;

	const openIssueCreateModalWithAnalytics = useCallback(
		(nextState?: GICPayload) => {
			const analyticsEvent = createAnalyticsEvent({});

			const attributes: Attributes = {
				gicViewState: nextState?.minimizableModalView,
				isSubtask: nextState?.isSubTaskCreationOpen,
				triggerPointKey: nextState?.triggerPointKey,
				...(nextState?.addChildWorkItemTriggerSource
					? { addChildWorkItemTriggerSource: nextState?.addChildWorkItemTriggerSource }
					: {}),
				...(nextState?.product ? { product: nextState.product } : {}),
				...(nextState?.actionSource ? { actionSource: nextState.actionSource } : {}),
			};

			fireTrackAnalyticsDeferred(analyticsEvent, 'issueCreateModal triggered', attributes);

			openIssueCreateModal(nextState);
		},
		[createAnalyticsEvent, openIssueCreateModal],
	);

	return [
		createState,
		{
			...otherActions,
			openIssueCreateModal: openIssueCreateModalWithAnalytics,
		},
	];
};

export const useOpenIssueCreateModal = (): ((nextState?: GICPayload) => void) => {
	const { openIssueCreateModal } = useTriggerIssueCreateModalActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setSitePickerState }] = useSitePickerContext();

	return useCallback(
		(nextState?: GICPayload) => {
			const analyticsEvent = createAnalyticsEvent({});

			const attributes: Attributes = {
				gicViewState: nextState?.minimizableModalView,
				isSubtask: nextState?.isSubTaskCreationOpen,
				triggerPointKey: nextState?.triggerPointKey,
				...(nextState?.product ? { product: nextState.product } : {}),
				...(nextState?.actionSource ? { actionSource: nextState.actionSource } : {}),
				...(nextState?.addChildWorkItemTriggerSource
					? { addChildWorkItemTriggerSource: nextState?.addChildWorkItemTriggerSource }
					: {}),
			};

			fireTrackAnalyticsDeferred(analyticsEvent, 'issueCreateModal triggered', attributes);

			// overrideCloudId only used for embedded GIC
			if (nextState?.payload?.overrideCloudId) {
				// set cloudId inside the state if there's an override
				setSitePickerState({
					selectedCloudId: nextState?.payload?.overrideCloudId,
				});
			}
			openIssueCreateModal(nextState);
		},
		[createAnalyticsEvent, openIssueCreateModal, setSitePickerState],
	);
};

export const TriggerIssueCreateModalSubscriber: SubscriberComponent<
	State,
	BoundActions<
		State,
		{
			restoreState: (
				stateToRestore: Partial<State>,
				isTransitioningBetweenExistingAndNewModal?: boolean,
			) => Action<State>;
			openIssueCreateModal: (nextState?: GICPayload) => Action<State>;
			closeIssueCreateModal: (hasIssueCreated?: boolean) => Action<State>;
			triggerFallbackToOldModal: (
				project: Project,
				options?: { forceLegacyCreateExperience?: boolean },
			) => Action<State>;
			switchToModernModal: (project: Project) => Action<State>;
			clearSectionMessage: () => Action<State>;
			setDisplayOnlyRequiredFields: (nextState: boolean) => Action<State>;
			setMinimizableModalView: (minimizableModalView: MinimizableModalView) => Action<State>;
			resetWasModalRetriggered: (applyNextState?: boolean) => Action<State>;
			resetShouldInitFetchConfig: () => Action<State>;
			setCallbackPayload: (
				callbackPayload: Required<GICPayload>['callbackPayload']['data'],
			) => Action<State>;
			setShouldPushMiniModalDiscoverability: (
				shouldPushMiniModalDiscoverability?: boolean,
			) => Action<State>;
			dismissModalSpotlight: () => Action<State>;
			setPersistentModalView: (
				persistentModalView: PersistentIssueCreateModalView,
			) => Action<State>;
			removeAlertFromAlertsToBeLinked: (alertId: string) => Action<State>;
			setSessionId: (sessionId: string) => Action<State>;
		}
	>,
	unknown
> = createSubscriber<State, Actions>(store);

export function withNewGICProvider<Props>(WrappedComponent: ComponentType<Props>) {
	return (props: Props): React.JSX.Element => {
		const [, { openIssueCreateModal, closeIssueCreateModal }] = useTriggerIssueCreateModal();

		return (
			<WrappedComponent
				openIssueCreateModal={openIssueCreateModal}
				closeIssueCreateModal={closeIssueCreateModal}
				currentIssueCreateVisibility
				{...props}
			/>
		);
	};
}
